export type CountryCode = 'NL' | 'DE' | 'FR' | 'GB' | 'BE' | 'DK' | 'IT' | 'CH'

export interface VatRate {
  code: string
  percentage: number
  label: string
}

export interface LocaleOption {
  locale: string
  label: string
}

export interface DocumentLabels {
  invoice: string
  order_response: string
  despatch_advice: string
  credit_note: string
}

export interface LocaleConfig {
  currency: string
  /** Backward-compatible default locale for this country. */
  locale: string
  /** Optional explicit locale choices. Omitted means this country has one locale. */
  localeOptions?: LocaleOption[]
  /** Optional UI document labels for non-default locales of a multilingual country. */
  docLabelsByLocale?: Record<string, DocumentLabels>
  /** Optional localized VAT option labels; percentages/codes remain country-owned. */
  vatRatesByLocale?: Record<string, VatRate[]>
  defaultVatCode: string
  defaultVatPct: number
  vatRates: VatRate[]
  docLabels: DocumentLabels
}

export const LOCALES: Record<CountryCode, LocaleConfig> = {
  NL: {
    currency: 'EUR', locale: 'nl-NL', defaultVatCode: 'S', defaultVatPct: 21,
    vatRates: [
      { code: 'S', percentage: 21, label: 'S — Standaard (21%)' },
      { code: 'L', percentage: 9,  label: 'L — Laag (9%)' },
      { code: 'Z', percentage: 0,  label: 'Z — Nultarief (0%)' },
      { code: 'E', percentage: 0,  label: 'E — Vrijgesteld' },
    ],
    docLabels: { invoice: 'Factuur', order_response: 'Orderbevestiging', despatch_advice: 'Pakbon', credit_note: 'Creditnota' },
  },
  DE: {
    currency: 'EUR', locale: 'de-DE', defaultVatCode: 'S', defaultVatPct: 19,
    vatRates: [
      { code: 'S', percentage: 19, label: 'S — Normalsatz (19%)' },
      { code: 'R', percentage: 7,  label: 'R — Ermäßigt (7%)' },
      { code: 'Z', percentage: 0,  label: 'Z — Nullsatz (0%)' },
      { code: 'E', percentage: 0,  label: 'E — Steuerbefreit' },
    ],
    docLabels: { invoice: 'Rechnung', order_response: 'Auftragsbestätigung', despatch_advice: 'Lieferschein', credit_note: 'Gutschrift' },
  },
  FR: {
    currency: 'EUR', locale: 'fr-FR', defaultVatCode: 'S', defaultVatPct: 20,
    vatRates: [
      { code: 'S',  percentage: 20,  label: 'S — Normal (20%)' },
      { code: 'I',  percentage: 10,  label: 'I — Intermédiaire (10%)' },
      { code: 'R',  percentage: 5.5, label: 'R — Réduit (5,5%)' },
      { code: 'SR', percentage: 2.1, label: 'SR — Super-réduit (2,1%)' },
      { code: 'Z',  percentage: 0,   label: 'Z — Exonéré (0%)' },
      { code: 'E',  percentage: 0,   label: 'E — Exonéré' },
    ],
    docLabels: { invoice: 'Facture', order_response: 'Confirmation de commande', despatch_advice: 'Bon de livraison', credit_note: 'Note de crédit' },
  },
  GB: {
    currency: 'GBP', locale: 'en-GB', defaultVatCode: 'S', defaultVatPct: 20,
    vatRates: [
      { code: 'S', percentage: 20, label: 'S — Standard (20%)' },
      { code: 'R', percentage: 5,  label: 'R — Reduced (5%)' },
      { code: 'Z', percentage: 0,  label: 'Z — Zero (0%)' },
      { code: 'E', percentage: 0,  label: 'E — Exempt' },
    ],
    docLabels: { invoice: 'Invoice', order_response: 'Order Confirmation', despatch_advice: 'Despatch Advice', credit_note: 'Credit Note' },
  },
  BE: {
    currency: 'EUR', locale: 'nl-BE',
    localeOptions: [
      { locale: 'nl-BE', label: 'Nederlands (nl-BE)' },
      { locale: 'fr-BE', label: 'Français (fr-BE)' },
    ],
    docLabelsByLocale: {
      'fr-BE': { invoice: 'Facture', order_response: 'Confirmation de commande', despatch_advice: 'Bon de livraison', credit_note: 'Note de crédit' },
    },
    defaultVatCode: 'S', defaultVatPct: 21,
    vatRates: [
      { code: 'S', percentage: 21, label: 'S — Normaal (21%)' },
      { code: 'I', percentage: 12, label: 'I — Intermediair (12%)' },
      { code: 'R', percentage: 6,  label: 'R — Verlaagd (6%)' },
      { code: 'Z', percentage: 0,  label: 'Z — Nultarief (0%)' },
      { code: 'E', percentage: 0,  label: 'E — Vrijgesteld' },
    ],
    vatRatesByLocale: {
      'fr-BE': [
        { code: 'S', percentage: 21, label: 'S — Normal (21%)' },
        { code: 'I', percentage: 12, label: 'I — Intermédiaire (12%)' },
        { code: 'R', percentage: 6, label: 'R — Réduit (6%)' },
        { code: 'Z', percentage: 0, label: 'Z — Taux zéro (0%)' },
        { code: 'E', percentage: 0, label: 'E — Exonéré' },
      ],
    },
    docLabels: { invoice: 'Factuur', order_response: 'Orderbevestiging', despatch_advice: 'Pakbon', credit_note: 'Creditnota' },
  },
  DK: {
    currency: 'DKK', locale: 'da-DK', defaultVatCode: 'S', defaultVatPct: 25,
    vatRates: [
      { code: 'S', percentage: 25, label: 'S — Standardsats (25%)' },
      { code: 'Z', percentage: 0,  label: 'Z — Nulsats (0%)' },
      { code: 'E', percentage: 0,  label: 'E — Fritaget' },
    ],
    docLabels: { invoice: 'Faktura', order_response: 'Ordrebekræftelse', despatch_advice: 'Følgeseddel', credit_note: 'Kreditnota' },
  },
  IT: {
    currency: 'EUR', locale: 'it-IT', defaultVatCode: 'S', defaultVatPct: 22,
    vatRates: [
      { code: 'S',  percentage: 22, label: 'S — Ordinaria (22%)' },
      { code: 'R',  percentage: 10, label: 'R — Ridotta (10%)' },
      { code: 'S2', percentage: 5,  label: 'S2 — Speciale (5%)' },
      { code: 'SR', percentage: 4,  label: 'SR — Super-ridotta (4%)' },
      { code: 'Z',  percentage: 0,  label: 'Z — Aliquota zero (0%)' },
      { code: 'E',  percentage: 0,  label: 'E — Esente' },
    ],
    docLabels: { invoice: 'Fattura', order_response: "Conferma d'ordine", despatch_advice: 'Documento di trasporto', credit_note: 'Nota di credito' },
  },
  CH: {
    // REQ-TESTREF-008: Switzerland has multiple language/locale profiles.
    // `locale` remains de-CH as the backwards-compatible default.
    currency: 'CHF', locale: 'de-CH',
    localeOptions: [
      { locale: 'de-CH', label: 'Deutsch (de-CH)' },
      { locale: 'fr-CH', label: 'Français (fr-CH)' },
      { locale: 'it-CH', label: 'Italiano (it-CH)' },
    ],
    docLabelsByLocale: {
      'fr-CH': { invoice: 'Facture', order_response: 'Confirmation de commande', despatch_advice: 'Bon de livraison', credit_note: 'Note de crédit' },
      'it-CH': { invoice: 'Fattura', order_response: "Conferma d'ordine", despatch_advice: 'Documento di trasporto', credit_note: 'Nota di credito' },
    },
    defaultVatCode: 'S', defaultVatPct: 8.1,
    vatRates: [
      { code: 'S',  percentage: 8.1, label: 'S — Normalsatz (8.1%)' },
      { code: 'R',  percentage: 3.8, label: 'R — Sondersatz Beherbergung (3.8%)' },
      { code: 'RR', percentage: 2.6, label: 'RR — Reduzierter Satz (2.6%)' },
      { code: 'Z',  percentage: 0,   label: 'Z — Nullsatz (0%)' },
      { code: 'E',  percentage: 0,   label: 'E — Steuerbefreit' },
    ],
    vatRatesByLocale: {
      'fr-CH': [
        { code: 'S', percentage: 8.1, label: 'S — Taux normal (8,1%)' },
        { code: 'R', percentage: 3.8, label: 'R — Hébergement (3,8%)' },
        { code: 'RR', percentage: 2.6, label: 'RR — Taux réduit (2,6%)' },
        { code: 'Z', percentage: 0, label: 'Z — Taux zéro (0%)' },
        { code: 'E', percentage: 0, label: 'E — Exonéré' },
      ],
      'it-CH': [
        { code: 'S', percentage: 8.1, label: 'S — Aliquota normale (8,1%)' },
        { code: 'R', percentage: 3.8, label: 'R — Alloggio (3,8%)' },
        { code: 'RR', percentage: 2.6, label: 'RR — Aliquota ridotta (2,6%)' },
        { code: 'Z', percentage: 0, label: 'Z — Aliquota zero (0%)' },
        { code: 'E', percentage: 0, label: 'E — Esente' },
      ],
    },
    docLabels: { invoice: 'Rechnung', order_response: 'Auftragsbestätigung', despatch_advice: 'Lieferschein', credit_note: 'Gutschrift' },
  },
}

export function localeOptionsForCountry(country: CountryCode): LocaleOption[] {
  const cfg = LOCALES[country]
  return cfg.localeOptions ?? [{ locale: cfg.locale, label: cfg.locale }]
}

export function countrySupportsLocale(country: CountryCode, locale: string): boolean {
  return localeOptionsForCountry(country).some(option => option.locale === locale)
}

export function countryForLocale(locale: string): CountryCode | undefined {
  return (Object.entries(LOCALES).find(([country]) => countrySupportsLocale(country as CountryCode, locale))?.[0] as CountryCode | undefined)
}

export function documentLabelsForLocale(country: CountryCode, locale: string): DocumentLabels {
  const cfg = LOCALES[country]
  return cfg.docLabelsByLocale?.[locale] ?? cfg.docLabels
}

export function vatRatesForLocale(country: CountryCode, locale: string): VatRate[] {
  const cfg = LOCALES[country]
  return cfg.vatRatesByLocale?.[locale] ?? cfg.vatRates
}

export const COUNTRY_OPTIONS: Array<{ value: CountryCode; flag: string; name: string }> = [
  { value: 'NL', flag: '🇳🇱', name: 'Nederland' },
  { value: 'DE', flag: '🇩🇪', name: 'Duitsland' },
  { value: 'FR', flag: '🇫🇷', name: 'Frankrijk' },
  { value: 'GB', flag: '🇬🇧', name: 'Engeland' },
  { value: 'BE', flag: '🇧🇪', name: 'België' },
  { value: 'DK', flag: '🇩🇰', name: 'Denemarken' },
  { value: 'IT', flag: '🇮🇹', name: 'Italië' },
  { value: 'CH', flag: '🇨🇭', name: 'Zwitserland' },
]
